import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const RealTimeStatusIndicator = () => {
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Simulate connection status changes
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate occasional disconnections
      const shouldDisconnect = Math.random() < 0.05; // 5% chance
      if (shouldDisconnect && connectionStatus === 'connected') {
        setConnectionStatus('disconnected');
        setTimeout(() => {
          setConnectionStatus('connected');
          setLastUpdated(new Date());
        }, 2000 + Math.random() * 3000); // Reconnect after 2-5 seconds
      } else if (connectionStatus === 'connected') {
        setLastUpdated(new Date());
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [connectionStatus]);

  // Simulate data updates
  useEffect(() => {
    if (connectionStatus === 'connected') {
      const updateInterval = setInterval(() => {
        setConnectionStatus('updating');
        setTimeout(() => {
          setConnectionStatus('connected');
          setLastUpdated(new Date());
        }, 500);
      }, 15000); // Update every 15 seconds

      return () => clearInterval(updateInterval);
    }
  }, [connectionStatus]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setConnectionStatus('updating');
    
    // Simulate API call
    setTimeout(() => {
      setConnectionStatus('connected');
      setLastUpdated(new Date());
      setIsRefreshing(false);
    }, 1000);
  };

  const getStatusConfig = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          icon: 'Wifi',
          color: 'text-success',
          bgColor: 'bg-success/10',
          label: 'Connected',
          pulse: false
        };
      case 'updating':
        return {
          icon: 'RefreshCw',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          label: 'Updating',
          pulse: true
        };
      case 'disconnected':
        return {
          icon: 'WifiOff',
          color: 'text-error',
          bgColor: 'bg-error/10',
          label: 'Disconnected',
          pulse: true
        };
      default:
        return {
          icon: 'Wifi',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          label: 'Unknown',
          pulse: false
        };
    }
  };

  const formatLastUpdated = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="flex items-center space-x-3">
      {/* Status Indicator */}
      <div className="flex items-center space-x-2">
        <div className={`flex items-center justify-center w-6 h-6 rounded-full ${statusConfig.bgColor}`}>
          <Icon 
            name={statusConfig.icon} 
            size={12} 
            className={`${statusConfig.color} ${statusConfig.pulse ? 'animate-pulse-subtle' : ''} ${connectionStatus === 'updating' ? 'animate-spin' : ''}`}
          />
        </div>
        
        {/* Desktop Status Text */}
        <div className="hidden sm:flex flex-col">
          <span className={`text-xs font-medium ${statusConfig.color}`}>
            {statusConfig.label}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatLastUpdated(lastUpdated)}
          </span>
        </div>
      </div>

      {/* Manual Refresh Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleManualRefresh}
        disabled={isRefreshing || connectionStatus === 'updating'}
        className="h-8 w-8 p-0"
        title="Refresh data"
      >
        <Icon 
          name="RefreshCw" 
          size={14} 
          className={isRefreshing || connectionStatus === 'updating' ? 'animate-spin' : ''}
        />
      </Button>

      {/* Mobile Status Tooltip */}
      <div className="sm:hidden">
        <div className="text-xs text-muted-foreground">
          {formatLastUpdated(lastUpdated)}
        </div>
      </div>
    </div>
  );
};

export default RealTimeStatusIndicator;